/**
 * Saisie de masse d'un DNS papier ou PDF (CP-11) — la grille, vue du moteur.
 *
 * Une ligne saisie est une ligne de fichier comme une autre : elle passe par
 * la même extraction (`extraireDns`), donc par les mêmes normalisations, les
 * mêmes lignes écartées (total, ayant droit nommé, sans nom) et le même
 * rapport de différences. La grille n'est qu'une feuille dont on connaît
 * déjà les colonnes ; « parti » y tient lieu de ligne rouge (CP-03).
 *
 * Pur : ni fichier, ni base.
 */
import { extraireDns, } from './extraction.js';
import { estErreurTableur } from './normalisation.js';
/**
 * Les colonnes de la grille, dans l'ordre du gabarit papier : la chargée
 * tape de gauche à droite, document sous les yeux. Pas de case « agent » :
 * sur papier, les ayants droit nommés se reconnaissent à leur fonction.
 */
export const CHAMPS_SAISIE = [
    'matricule',
    'nom',
    'prenom',
    'fonction',
    'nbConjoint',
    'nbEnfant',
    'dateNaissance',
    'sexe',
    'lieuAffectation',
    'dateEntree',
    'dateDepart',
    /**
     * En dernier : un DNS papier (un état nominatif de paie) le porte presque
     * toujours, et c'est là qu'on le trouve — les classeurs Excel du corpus
     * laissent la colonne vide dans 250 cas sur 278. N'est montré et
     * enregistré que pour `salaire:read`.
     */
    'salaireBrut',
];
/**
 * Les deux champs où une erreur de lecture ne se voit pas à l'œil : `8` lu
 * `3` dans un matricule, `1974` lu `1914` dans une date. Pré-remplis par
 * l'OCR, ils se vérifient un par un — les autres se relisent d'un regard.
 */
export const CHAMPS_A_VERIFIER = [
    'matricule',
    'dateNaissance',
];
export function ligneVide() {
    return {
        matricule: '',
        nom: '',
        prenom: '',
        fonction: '',
        nbConjoint: '',
        nbEnfant: '',
        dateNaissance: '',
        sexe: '',
        lieuAffectation: '',
        dateEntree: '',
        dateDepart: '',
        salaireBrut: '',
        parti: false,
        aVerifier: [],
        confirmeParRegistre: false,
    };
}
/**
 * Une ligne de grille relue depuis la base, complétée des champs qu'elle
 * n'avait pas encore — un brouillon enregistré avant l'ajout du salaire n'a
 * pas cette colonne, et ne doit pas faire tomber la saisie.
 */
export function completerLigneSaisie(brute) {
    // Un champ présent mais `undefined` ne doit pas écraser la valeur par défaut.
    const definis = Object.fromEntries(Object.entries(brute).filter(([, v]) => v !== undefined));
    return { ...ligneVide(), ...definis };
}
export function ligneEstVide(ligne) {
    return CHAMPS_SAISIE.every((champ) => ligne[champ].trim() === '');
}
/**
 * La grille sans ses lignes vides de fin. Celles du milieu restent : le
 * numéro d'une ligne est celui qu'elle porte sur le document.
 */
export function sansLignesDeFin(lignes) {
    let fin = lignes.length;
    while (fin > 0) {
        const derniere = lignes[fin - 1];
        if (derniere !== undefined && !ligneEstVide(derniere))
            break;
        fin -= 1;
    }
    return lignes.slice(0, fin);
}
const NOM_FEUILLE = 'SAISIE';
const MAPPING_SAISIE = {
    feuille: NOM_FEUILLE,
    premiereLigne: 1,
    colonnes: Object.fromEntries(CHAMPS_SAISIE.map((champ, index) => [champ, index])),
};
/** La grille comme une feuille sans en-tête : une colonne par champ, « parti » en rouge. */
export function feuilleDepuisSaisie(lignes) {
    return {
        nom: NOM_FEUILLE,
        lignes: lignes.map((ligne) => CHAMPS_SAISIE.map((champ) => {
            const texte = ligne[champ].trim();
            // Un bloc collé depuis Excel peut apporter un « #REF! » : ce n'est pas une valeur.
            return {
                valeur: texte === '' || estErreurTableur(texte) ? null : texte,
                rouge: ligne.parti,
            };
        })),
    };
}
/** L'extraction d'une saisie : `numeroLigne` est le numéro de la ligne dans la grille. */
export function extraireSaisie(lignes) {
    return extraireDns([feuilleDepuisSaisie(lignes)], MAPPING_SAISIE);
}
/** Ce qui reste à vérifier champ par champ avant de terminer la saisie (OCR, condition 3). */
export function resteAVerifier(lignes) {
    return lignes.reduce((n, l) => n + (ligneEstVide(l) ? 0 : l.aVerifier.length), 0);
}
//# sourceMappingURL=saisie.js.map